
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@/contexts/AuthContext";
import { logBreadcrumb } from "@/lib/sentry";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredUserType?: 'marketer' | 'creator';
  allowAdminUserTypeBypass?: boolean;
  allowAdminOnboardingBypass?: boolean;
}

const ProtectedRoute = ({
  children,
  requiredUserType,
  allowAdminUserTypeBypass = false,
  allowAdminOnboardingBypass = false,
}: ProtectedRouteProps) => {
  const { user, userDetails, creatorDetails, loading, isAdmin, adminLoading } = useAuth();
  const { userType } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const creatorOnboardingRedirectPath = `/creator-onboarding?next=${encodeURIComponent(
    `${location.pathname}${location.search}${location.hash}`
  )}`;
  const [creatorOnboardingStatus, setCreatorOnboardingStatus] = useState<{
    tiktokConnected: boolean;
    tiktokUsernameFilled: boolean;
    instagramConnected: boolean;
    youtubeConnected: boolean;
    stripeConnected: boolean;
    paypalConnected: boolean;
    paypalEmailFilled: boolean;
    loading: boolean;
  }>({
    tiktokConnected: false,
    tiktokUsernameFilled: false,
    instagramConnected: false,
    youtubeConnected: false,
    stripeConnected: false,
    paypalConnected: false,
    paypalEmailFilled: false,
    loading: true,
  });
  const [marketerApprovalStatus, setMarketerApprovalStatus] = useState<{
    isApproved: boolean;
    loading: boolean;
  }>({
    isApproved: false,
    loading: true,
  });
  const [dashboardHistoryAccess, setDashboardHistoryAccess] = useState({
    userId: null as string | null,
    creatorDetails: null as typeof creatorDetails,
    allowed: false,
    loading: false,
  });
  const missingCreatorSocial = !creatorOnboardingStatus.tiktokUsernameFilled &&
    !creatorOnboardingStatus.instagramConnected && !creatorOnboardingStatus.youtubeConnected;
  const needsDashboardHistoryAccess = userDetails?.user_type === "creator" &&
    location.pathname === "/creator-dashboard" && !creatorOnboardingStatus.loading && missingCreatorSocial;

  const historyUserId = user?.id;
  useEffect(() => {
    if (!historyUserId || !needsDashboardHistoryAccess) return;
    let cancelled = false;
    setDashboardHistoryAccess({ userId: historyUserId, creatorDetails, allowed: false, loading: true });
    const checkHistory = async () => {
      try {
        // A retained archived account proves this creator previously connected a
        // social identity. A fresh signup still completes social onboarding.
        const { data, error } = await supabase.from("creator_tiktok_accounts")
          .select("id").eq("creator_id", historyUserId).eq("is_archived", true).limit(1);
        if (!cancelled) {
          setDashboardHistoryAccess({ userId: historyUserId, creatorDetails, allowed: !error && !!data?.length, loading: false });
        }
      } catch {
        if (!cancelled) setDashboardHistoryAccess({ userId: historyUserId, creatorDetails, allowed: false, loading: false });
      }
    };
    void checkHistory();
    return () => { cancelled = true; };
  }, [needsDashboardHistoryAccess, historyUserId, creatorDetails]);

  // Check marketer approval status
  useEffect(() => {
    // Wait for user and userDetails to be fully loaded
    if (!user) {
      setMarketerApprovalStatus({ isApproved: false, loading: false });
      return;
    }
    if (!userDetails) {
      return;
    }

    // If not a marketer, mark as approved (not loading)
    if (userDetails.user_type !== "marketer") {
      setMarketerApprovalStatus({ isApproved: true, loading: false });
      return;
    }

    // Use approval status from userDetails (already fetched in AuthContext)
    setMarketerApprovalStatus({
      isApproved: userDetails.status === "approved",
      loading: false,
    });
  }, [user, userDetails]);

  // Check creator onboarding status using shared creatorDetails
  useEffect(() => {
    const checkCreatorOnboarding = () => {
      // Wait for both user and userDetails to be fully loaded
      if (!user) {
        setCreatorOnboardingStatus((prev) => ({ ...prev, loading: false }));
        return;
      }
      if (!userDetails) {
        setCreatorOnboardingStatus((prev) => ({ ...prev, loading: true }));
        return; // Keep loading state until both are available
      }

      // If not a creator, mark as not loading
      if (userDetails.user_type !== "creator") {
        setCreatorOnboardingStatus((prev) => ({ ...prev, loading: false }));
        return;
      }

      // If creator details haven't loaded yet, wait
      if (!creatorDetails) {
        setCreatorOnboardingStatus((prev) => ({ ...prev, loading: true }));
        return;
      }

      // Use data from shared creatorDetails and userDetails.
      const paypalConnected = !!(
        creatorDetails.paypal_payer_id ||
        creatorDetails.paypal_email ||
        creatorDetails.paypal_connected
      );
      const paypalEmailFilled = !!creatorDetails.paypal_email;
      const tiktokUsernameFilled = !!creatorDetails.tiktok_username;
      const tiktokAccountsConnected = !!creatorDetails.tiktok_accounts_connected;
      const instagramAccountsConnected = !!creatorDetails.instagram_accounts_connected;
      const youtubeAccountsConnected = !!creatorDetails.youtube_accounts_connected;

      setCreatorOnboardingStatus({
        tiktokConnected: tiktokUsernameFilled || tiktokAccountsConnected || false,
        tiktokUsernameFilled: tiktokUsernameFilled || tiktokAccountsConnected,
        instagramConnected: instagramAccountsConnected,
        youtubeConnected: youtubeAccountsConnected,
        stripeConnected: creatorDetails.stripe_onboarding_complete || false,
        paypalConnected,
        paypalEmailFilled,
        loading: false,
      });
    };

    checkCreatorOnboarding();
  }, [user, userDetails, creatorDetails]);

  // Handle browser back/forward navigation for creators
  useEffect(() => {
    const handlePopState = () => {
      // Small delay to ensure React has updated the location
      setTimeout(() => {
        const actualUserType = userDetails?.user_type;
        const isCreatorOnboardingPage =
          window.location.pathname === "/creator-onboarding";

        if (
          actualUserType === "creator" &&
          !isAdmin &&
          !isCreatorOnboardingPage &&
          window.location.pathname !== "/account" &&
          window.location.pathname !== "/creator-dashboard" &&
          !creatorOnboardingStatus.loading
        ) {
          const {
            tiktokUsernameFilled,
            instagramConnected,
            youtubeConnected,
          } = creatorOnboardingStatus;

          if (!tiktokUsernameFilled && !instagramConnected && !youtubeConnected) {
            logBreadcrumb("ProtectedRoute redirect (popstate): creator missing social", {
              path: window.location.pathname,
            });
            navigate(creatorOnboardingRedirectPath, { replace: true });
          }
        }
      }, 100);
    };

    // Only add listener if creator onboarding status is loaded and user is a creator
    if (
      userDetails?.user_type === "creator" &&
      !isAdmin &&
      !creatorOnboardingStatus.loading
    ) {
      window.addEventListener("popstate", handlePopState);

      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [
    userDetails,
    creatorOnboardingStatus,
    navigate,
    creatorOnboardingRedirectPath,
    isAdmin,
  ]);

  // Show loading state while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // If user is not authenticated, redirect to auth page
  if (!user) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/auth" replace state={{ from }} />;
  }

  // If we have a user but no userDetails yet, show loading briefly
  if (user && !userDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Block suspended accounts regardless of session state
  if (userDetails?.is_banned) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-semibold text-white mb-3">Account Suspended</h1>
          <p className="text-zinc-400">
            Your account has been suspended. Contact{" "}
            <a href="mailto:support@audiotrack.app" className="text-blue-400 underline">
              support@audiotrack.app
            </a>{" "}
            if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  // Persisted account type is authoritative; cached role choices never grant another workspace.
  if (userDetails?.user_type === "agency") {
    return <Navigate to="/agency" replace />;
  }

  const isAdminRoute = location.pathname.startsWith("/admin");
  const actualUserType = userDetails?.user_type;
  const previewParams = new URLSearchParams(location.search);
  const previewCreatorId = previewParams.get("previewCreatorId");
  const previewMarketerId = previewParams.get("previewMarketerId");
  const requestedPreviewId =
    requiredUserType === "creator"
      ? previewCreatorId || localStorage.getItem("admin-mock-creator-id")
      : requiredUserType === "marketer" || location.pathname === "/dashboard"
        ? previewMarketerId || localStorage.getItem("admin-mock-marketer-id")
        : null;
  const hasRequestedAdminPreview = Boolean(requestedPreviewId);
  const needsPotentialAdminBypass = Boolean(
    allowAdminOnboardingBypass ||
    (allowAdminUserTypeBypass && requiredUserType && actualUserType !== requiredUserType)
  );

  if (
    (isAdminRoute || hasRequestedAdminPreview || needsPotentialAdminBypass) &&
    adminLoading
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const isAuthorizedAdminPreview = Boolean(
    isAdmin && hasRequestedAdminPreview
  );

  if (isAdminRoute && isAdmin) {
    return <>{children}</>;
  }

  if (
    isAuthorizedAdminPreview ||
    (isAdmin && (allowAdminUserTypeBypass || allowAdminOnboardingBypass))
  ) {
    return <>{children}</>;
  }

  // Onboarding can create a missing creator row, so it must remain reachable
  // while the shared creator profile is absent or could not be fetched.
  const isCreatorOnboardingPage = location.pathname === "/creator-onboarding";
  // Show loading state while creator onboarding or marketer approval is being determined
  if ((actualUserType === "creator" && !isCreatorOnboardingPage && !creatorDetails) ||
    (!isCreatorOnboardingPage && creatorOnboardingStatus.loading) || marketerApprovalStatus.loading ||
    (needsDashboardHistoryAccess &&
      (dashboardHistoryAccess.userId !== user.id || dashboardHistoryAccess.creatorDetails !== creatorDetails || dashboardHistoryAccess.loading))) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Check if creator needs to complete onboarding (skip for creator-onboarding page itself)
  // Keep account settings accessible after the last social account is unlinked
  // so creators can immediately complete OAuth for a replacement account.
  const isAccountSettingsPage = location.pathname === "/account";
  const isCreatorHistoryPage = location.pathname === "/creator-dashboard" &&
    dashboardHistoryAccess.userId === user.id && dashboardHistoryAccess.creatorDetails === creatorDetails && dashboardHistoryAccess.allowed;
  if (actualUserType === "creator" && !isCreatorOnboardingPage && !isAccountSettingsPage && !isCreatorHistoryPage) {
    const {
      tiktokUsernameFilled,
      instagramConnected,
      youtubeConnected,
    } = creatorOnboardingStatus;

    if (!tiktokUsernameFilled && !instagramConnected && !youtubeConnected) {
      logBreadcrumb("ProtectedRoute redirect: creator missing social", {
        from: location.pathname,
      });
      return <Navigate to={creatorOnboardingRedirectPath} replace />;
    }
  }

  // Check if marketer is approved (skip for marketer-onboarding page itself)
  const isMarketerOnboardingPage = location.pathname === "/marketer-onboarding";
  if (actualUserType === "marketer" && !isMarketerOnboardingPage) {
    if (!marketerApprovalStatus.isApproved) {
      return <Navigate to="/marketer-onboarding" replace />;
    }
  }

  // If a specific user type is required and user doesn't match
  if (requiredUserType && actualUserType !== requiredUserType) {
    if (!(isAdmin && allowAdminUserTypeBypass)) {
      // Redirect creators to marketplace, marketers to dashboard
      const redirectTo = actualUserType === "creator" ? "/marketplace" : "/dashboard";
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
